import { useEffect } from "react";
import { useQuery, useQueryClient, QueryClient } from "@tanstack/react-query";
import { authorizedAxios, useWebSocketContext } from "@moonscape/shared";
import { useAuth0 } from "@auth0/auth0-react";

export const TAX_OPERATION_STATUS_KEY = "tax_operation_status";

/** A `running` row older than this is a stalled run, not a live one (TaxScreen uses the same hour). */
const RUNNING_FRESH_MS = 60 * 60 * 1000;
/** Poll while a calc runs: the dropped-socket heartbeat. A calc takes 5-8 s, so 15 s saw nothing. */
const RUNNING_POLL_MS = 3000;
/** Coalesce a burst of tax_progress events (several stages flip within 10 ms) into one fetch. */
const POKE_MS = 250;

export const isLiveRun = (status: any, now = Date.now()) =>
  status?.status === "running" &&
  now - new Date(status?.last_updated ?? 0).getTime() < RUNNING_FRESH_MS;

/** Queries whose figures a finished calc rewrites. Our own status key is excluded. */
const TAX_FIGURE_KEY = /^(cgt|tax|uk_|us_|income|safeHarbor|audit|pricing)/i;
export const isTaxFigureQuery = (key: readonly unknown[]) =>
  typeof key[0] === "string" &&
  key[0] !== TAX_OPERATION_STATUS_KEY &&
  TAX_FIGURE_KEY.test(key[0]);

// Module-level so the many mounted instances of this hook share one poke and
// one "completed" handling per calc instead of each firing its own.
let pokeTimer: ReturnType<typeof setTimeout> | null = null;
let lastSeen: string | null = null;

/**
 * A newly observed `completed` (status or last_updated changed since the last
 * fetch this page saw) means the report rows are committed: refetch the figures.
 * The first fetch on page load only records the key; those figures are fresh.
 */
export const onStatusFetched = (queryClient: QueryClient, status: any) => {
  if (!status) return;
  const key = `${status.status}|${status.last_updated ?? ""}`;
  const changed = lastSeen !== null && lastSeen !== key;
  lastSeen = key;
  if (changed && status.status === "completed") {
    queryClient.invalidateQueries({
      predicate: (q) => isTaxFigureQuery(q.queryKey),
    });
  }
};

export const useTaxOperationStatus = () => {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently } =
    useAuth0();
  const { messages } = useWebSocketContext();
  const queryClient = useQueryClient();

  const email = user?.email;

  const getTaxOperationStatus = async (): Promise<any> => {
    const token = await getAccessTokenSilently();
    const res = await authorizedAxios(token).get(`/tax/status`, {
      params: { email: email },
    });
    return res.data;
  };

  const {
    isLoading: isQueryLoading,
    error,
    data: operationStatus,
    refetch,
  } = useQuery({
    queryKey: [TAX_OPERATION_STATUS_KEY],
    queryFn: getTaxOperationStatus,
    enabled: !!email && isAuthenticated && !isLoading,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      isLiveRun(query.state.data) ? RUNNING_POLL_MS : false,
  });

  // WS pokes, REST truth (ui-state-invariants I10): a tax_progress event only
  // schedules a /tax/status refetch; the fetched row is what renders. Trailing,
  // so the last event of a burst (e.g. `completed`) is never dropped.
  useEffect(() => {
    if (!messages?.some((m: any) => m?.type === "tax_progress")) return;
    if (pokeTimer) return;
    pokeTimer = setTimeout(() => {
      pokeTimer = null;
      queryClient.invalidateQueries({ queryKey: [TAX_OPERATION_STATUS_KEY] });
    }, POKE_MS);
  }, [messages, queryClient]);

  useEffect(() => {
    onStatusFetched(queryClient, operationStatus);
  }, [operationStatus, queryClient]);

  return { isQueryLoading, error, operationStatus, email, refetch };
};
